import React, { useEffect, useRef, useState, useCallback } from 'react';
import './BundleSlider.css';
import LazyImage from '../../components/LazyImage/LazyImage';
import VideoPlayer from '../../shared/components/VideoPlayer';
import { formatDuration, getGradientBackgroundImage } from '../../shared/utils/function';
import { attachHorizontalSwipeGesture } from '../../shared/utils/horizontalSwipeGesture';
import { MEDIA_TYPE } from '../../shared/constants/common';

/** Bottom band of the video frame where native controls / timeline live (cross-browser heuristic). */
const VIDEO_CONTROLS_ZONE_RATIO = 0.65;
const SWIPE_UNLOCK_DELAY_MS = 150;

const videoHasStartedPlayback = (video) => {
  if (!video) return false;
  return (
    video.classList.contains('video-has-started') ||
    video.classList.contains('video-is-playing') ||
    video.classList.contains('video-is-seeking-playback')
  );
};

const isPointerInVideoControlsZone = (video, clientY) => {
  if (!video || clientY == null) return false;
  const rect = video.getBoundingClientRect();
  if (rect.height <= 0) return false;
  return (clientY - rect.top) / rect.height >= VIDEO_CONTROLS_ZONE_RATIO;
};

const shouldBlockSwipeForVideoGesture = (video, clientX, clientY, dx = 0) => {
  if (!video || !videoHasStartedPlayback(video)) return false;
  if (video.seeking) return true;
  if (isPointerInVideoControlsZone(video, clientY)) return true;
  // Paused scrub: horizontal drag on a started video is timeline interaction, not carousel swipe.
  if (video.paused && Math.abs(dx) > 15) return true;
  return false;
};


const BundleSlider = ({
  media,
  className = '',
  showControls = false,
  isLock = false,
  isShowDuration = false,
  hideSliderDots = false,
  gradientSeed = null,
  /** 'below': dots under the carousel (feed). 'overlay': dots inside the carousel bottom (chat bubbles). */
  dotsPlacement = 'below',
  /**
   * Chat bubbles: avoid custom center play + native controls stacking (double play icon on iOS).
   * Rely on the native video UI only.
   */
  chatNativeVideoChrome = false,
  onMessagingVideoPlayingChange,
  onVideoPlaybackStartedChange,
  onCurrentIndexChange,
  /** When set, image slides are tappable (e.g. chat fullscreen preview). */
  onImageClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [playingVideos, setPlayingVideos] = useState({}); // Track playing state per video index
  const [hasVideoStarted, setHasVideoStarted] = useState({});
  const wrapperRef = useRef(null);
  /** True when the user dragged far enough that we treated it as a swipe (suppresses tap/click). */
  const didSwipeRef = useRef(false);
  /** Blocks bundle swipes while the user scrubs / seeks on the active video slide. */
  const isSwipeLockedRef = useRef(false);
  const swipeUnlockTimerRef = useRef(null);
  const activeVideoPointerRef = useRef(null);
  const seekingVideoCountRef = useRef(0);

  const isChatCarousel = dotsPlacement === 'overlay';

  const lockSwipe = useCallback(() => {
    if (swipeUnlockTimerRef.current) {
      clearTimeout(swipeUnlockTimerRef.current);
      swipeUnlockTimerRef.current = null;
    }
    isSwipeLockedRef.current = true;
  }, []);

  const scheduleSwipeUnlock = useCallback(() => {
    if (seekingVideoCountRef.current > 0) return;
    if (swipeUnlockTimerRef.current) {
      clearTimeout(swipeUnlockTimerRef.current);
    }
    swipeUnlockTimerRef.current = setTimeout(() => {
      isSwipeLockedRef.current = false;
      swipeUnlockTimerRef.current = null;
    }, SWIPE_UNLOCK_DELAY_MS);
  }, []);

  const isSwipeAllowed = useCallback(() => !isSwipeLockedRef.current, []);

  const handleVideoSeekingChange = useCallback((seeking) => {
    if (seeking) {
      seekingVideoCountRef.current += 1;
      lockSwipe();
      return;
    }

    seekingVideoCountRef.current = Math.max(0, seekingVideoCountRef.current - 1);
    scheduleSwipeUnlock();
  }, [lockSwipe, scheduleSwipeUnlock]);

  const getPointerCoords = (e) => {
    if (e.clientX != null && e.clientY != null) {
      return { x: e.clientX, y: e.clientY };
    }
    const touch = e.touches?.[0] ?? e.changedTouches?.[0];
    if (!touch) return null;
    return { x: touch.clientX, y: touch.clientY };
  };

  const handleVideoPointerDown = useCallback((e) => {
    const video = e.currentTarget.querySelector('video');
    if (!video) return;

    const coords = getPointerCoords(e);
    if (!coords) return;

    activeVideoPointerRef.current = {
      video,
      startX: coords.x,
      startY: coords.y,
      blocked: shouldBlockSwipeForVideoGesture(video, coords.x, coords.y, 0),
    };

    if (activeVideoPointerRef.current.blocked) {
      lockSwipe();
    }
  }, [lockSwipe]);

  const handleVideoPointerMove = useCallback((e) => {
    const state = activeVideoPointerRef.current;
    if (!state) return;

    const coords = getPointerCoords(e);
    if (!coords) return;

    const dx = coords.x - state.startX;
    if (
      state.blocked ||
      shouldBlockSwipeForVideoGesture(state.video, coords.x, coords.y, dx)
    ) {
      state.blocked = true;
      lockSwipe();
    }
  }, [lockSwipe]);

  const handleVideoPointerEnd = useCallback(() => {
    const state = activeVideoPointerRef.current;
    activeVideoPointerRef.current = null;

    if (state?.blocked && !state.video?.seeking) {
      scheduleSwipeUnlock();
    }
  }, [scheduleSwipeUnlock]);

  const isInteractiveTarget = useCallback((target) => {
    if (!target) return false;
    // Skip swipe for dot buttons, links, inputs, and explicitly-marked controls.
    // Video slides must remain swipeable (horizontal gesture defers until movement is committed).
    const btn = target.closest?.('button');
    if (btn && !btn.classList.contains('cm_chat_media_img_button')) {
      return true;
    }
    return Boolean(
      target.closest?.('a, input, textarea, select, [data-prevent-swipe="true"]')
    );
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === media.length - 1 ? prevIndex : prevIndex + 1
    );
  }, [media.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? prevIndex : prevIndex - 1
    );
  }, []);

  const SWIPE_THRESHOLD_PX = isChatCarousel ? 30 : 40;

  // Shared swipe detection logic (mouse drag on desktop)
  const handleSwipe = useCallback((startPos, endPos) => {
    if (isSwipeLockedRef.current) return;
    if (startPos == null || endPos == null) return;

    const distance = startPos - endPos;
    const isLeftSwipe = distance > SWIPE_THRESHOLD_PX;
    const isRightSwipe = distance < -SWIPE_THRESHOLD_PX;

    if (isLeftSwipe) {
      didSwipeRef.current = true;
      nextSlide();
    } else if (isRightSwipe) {
      didSwipeRef.current = true;
      prevSlide();
    }
  }, [nextSlide, prevSlide, SWIPE_THRESHOLD_PX]);

  // Mouse event handlers for desktop drag functionality
  const handleMouseDown = (e) => {
    // Don't start dragging if clicking on dot/control buttons – but allow the image wrapper button
    const btn = e.target.closest('button');
    if (btn && (btn.classList.contains('bundle-slider-dot') || btn.hasAttribute('data-prevent-swipe'))) {
      return;
    }

    const activeVideo = e.target.closest?.('video');
    if (activeVideo && videoHasStartedPlayback(activeVideo)) {
      const coords = { x: e.clientX, y: e.clientY };
      const blocked = shouldBlockSwipeForVideoGesture(activeVideo, coords.x, coords.y, 0);
      activeVideoPointerRef.current = {
        video: activeVideo,
        startX: coords.x,
        startY: coords.y,
        blocked,
      };
      if (blocked) {
        lockSwipe();
        return;
      }
    }

    didSwipeRef.current = false;
    setIsDragging(true);
    setMouseEnd(null);
    setMouseStart(e.clientX);

    // Preserve unobstructed clicks on video / play overlay; only block default for non-video drags.
    if (!e.target.closest?.('video, .video-wrapper')) {
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    const pointerState = activeVideoPointerRef.current;
    if (pointerState) {
      const coords = getPointerCoords(e);
      if (coords) {
        const dx = coords.x - pointerState.startX;
        if (
          !pointerState.blocked &&
          shouldBlockSwipeForVideoGesture(pointerState.video, coords.x, coords.y, dx)
        ) {
          pointerState.blocked = true;
          lockSwipe();
          setIsDragging(false);
          setMouseStart(null);
          setMouseEnd(null);
          return;
        }
      }

      if (pointerState.blocked) {
        handleVideoPointerMove(e);
        return;
      }
    }

    if (isDragging) {
      setMouseEnd(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (activeVideoPointerRef.current?.blocked) {
      handleVideoPointerEnd();
      return;
    }

    activeVideoPointerRef.current = null;

    if (!isDragging) {
      return;
    }

    handleSwipe(mouseStart, mouseEnd);

    setIsDragging(false);
    setMouseStart(null);
    setMouseEnd(null);
  };

  const handleMouseLeave = () => {
    if (activeVideoPointerRef.current?.blocked) {
      handleVideoPointerEnd();
    }
    activeVideoPointerRef.current = null;
    setIsDragging(false);
    setMouseStart(null);
    setMouseEnd(null);
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex(index);
  };

  // Keyboard navigation handler for laptop users
  const handleKeyDown = (e) => {
    if (isSwipeLockedRef.current) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextSlide();
    }
  };

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Touch swipe (capture phase in chat so horizontal gestures win over the message list scroll).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    // Reset didSwipe on each new touch start so taps work after a previous swipe
    const onTouchStart = () => { didSwipeRef.current = false; };
    el.addEventListener('touchstart', onTouchStart, { passive: true });

    const cleanup = attachHorizontalSwipeGesture(el, {
      onSwipeLeft: () => { didSwipeRef.current = true; nextSlide(); },
      onSwipeRight: () => { didSwipeRef.current = true; prevSlide(); },
      isInteractiveTarget,
      isSwipeAllowed,
      useCapture: isChatCarousel,
      chatMode: isChatCarousel,
    });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      cleanup();
    };
  }, [nextSlide, prevSlide, isInteractiveTarget, isSwipeAllowed, isChatCarousel]);

  useEffect(() => () => {
    if (swipeUnlockTimerRef.current) {
      clearTimeout(swipeUnlockTimerRef.current);
    }
  }, []);

  // Add global mouse event listeners for proper drag handling
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, mouseStart, mouseEnd]);

  // Add keyboard event listeners for laptop navigation
  useEffect(() => {
    // Only add keyboard listeners for non-mobile devices
    if (!isMobile) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, currentIndex, media.length]);

  // Reset playing state for videos not on current slide when slide changes
  useEffect(() => {
    activeVideoPointerRef.current = null;
    seekingVideoCountRef.current = 0;
    isSwipeLockedRef.current = false;
    if (swipeUnlockTimerRef.current) {
      clearTimeout(swipeUnlockTimerRef.current);
      swipeUnlockTimerRef.current = null;
    }

    setPlayingVideos(prev => {
      const updated = { ...prev };
      // Keep only the current slide's playing state, reset others
      Object.keys(updated).forEach(index => {
        if (parseInt(index) !== currentIndex) {
          delete updated[index];
        }
      });
      return updated;
    });
  }, [currentIndex]);

  useEffect(() => {
    onMessagingVideoPlayingChange?.(false);
    onCurrentIndexChange?.(currentIndex);
  }, [currentIndex, onMessagingVideoPlayingChange, onCurrentIndexChange]);

  if (!media || media.length === 0) {
    return null;
  }

  const overlayDotsClass = dotsPlacement === 'overlay' ? ' bundle-slider-dots-overlay' : '';
  const wrapperClassName =
    `bundle-slider bundle-slider-wrapper${overlayDotsClass}${isDragging ? ' grabbing' : ' grab'} ${className}`.trim();

  const dotsNode =
    media.length > 1 && !hideSliderDots ? (
      <div className="bundle-slider-dots">
        {media.length <= 7 ? (
          // Show all dots if 7 or fewer items
          media.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => handleDotClick(index, e)}
              className={`bundle-slider-dot ${index === currentIndex ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))
        ) : (
          // Show sliding window of 7 dots for more items
          (() => {
            const totalItems = media.length;
            const maxDots = 7;
            const startIndex = Math.max(0, Math.min(currentIndex - 3, totalItems - maxDots));

            return Array.from({ length: maxDots }, (_, dotIndex) => {
              const actualIndex = startIndex + dotIndex;
              const isActive = actualIndex === currentIndex;

              return (
                <button
                  key={actualIndex}
                  type="button"
                  onClick={(e) => handleDotClick(actualIndex, e)}
                  className={`bundle-slider-dot ${isActive ? 'active' : ''}`}
                  aria-label={`Go to slide ${actualIndex + 1}`}
                />
              );
            });
          })()
        )}
      </div>
    ) : null;

  return (
    <>
      <div
        className={wrapperClassName}
        tabIndex={0}
        ref={wrapperRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
      >
        <div
          className="bundle-slider-content"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {isLock && gradientSeed ? (
            // Show gradient background when locked
            <div className="bundle-slider-item media-grid-locked-content">
              <div className='cm_blur_16'></div>
              <img
                alt="..."
                src={getGradientBackgroundImage(currentIndex)} />
            </div>
          ) : (
            media.map((mediaItem, index) => (
              <div key={index} className="bundle-slider-item">
                {mediaItem.type_file === MEDIA_TYPE.IMAGE ? (
                  onImageClick ? (
                    <button
                      type="button"
                      className="cm_chat_media_img_button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Only open preview on genuine taps, not after a swipe gesture
                        if (!didSwipeRef.current) {
                          onImageClick(index, mediaItem);
                        }
                      }}
                      aria-label={`View image ${index + 1}`}
                    >
                      <LazyImage
                        src={mediaItem.src}
                        alt={`Bundle content ${index + 1}`}
                        className="cm-post-height bundle-slider-media"
                        onError={(e) => {
                          const fallback = document.createElement('div');
                          fallback.className = 'bundle-slider-fallback';
                          fallback.textContent = 'Image not available';
                          e.target.parentNode?.replaceChild(fallback, e.target);
                        }}
                      />
                    </button>
                  ) : (
                    <LazyImage
                      src={mediaItem.src}
                      alt={`Bundle content ${index + 1}`}
                      className='cm-post-height bundle-slider-media'
                      onError={(e) => {
                        const fallback = document.createElement('div');
                        fallback.className = 'bundle-slider-fallback';
                        fallback.textContent = 'Image not available';
                        e.target.parentNode?.replaceChild(fallback, e.target);
                      }}
                    />
                  )
                ) : (
                  <div
                    className="bundle-slider-video-slide"
                    onPointerDown={handleVideoPointerDown}
                    onPointerMove={handleVideoPointerMove}
                    onPointerUp={handleVideoPointerEnd}
                    onPointerCancel={handleVideoPointerEnd}
                    onClickCapture={(e) => {
                      if (didSwipeRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <VideoPlayer
                      src={mediaItem.src}
                      poster={mediaItem.poster || null}
                      className="cm-post-height video-no-timeline bundle-slider-media"
                      showControls={showControls}
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      disablePictureInPicture
                      disableRemotePlayback
                      preventContextMenu
                      muted
                      loop
                      playsInline
                      fallbackText='Video unavailable'
                      showDurationBadge={!isLock && !chatNativeVideoChrome}
                      onSeekingChange={handleVideoSeekingChange}
                      onPlayStarted={() => {
                        setPlayingVideos(prev => ({ ...prev, [index]: true }));
                        setHasVideoStarted(prev => ({ ...prev, [index]: true }));
                        if (index === currentIndex) {
                          onMessagingVideoPlayingChange?.(true);
                          onVideoPlaybackStartedChange?.(true);
                        }
                      }}
                      onPause={() => {
                        setPlayingVideos(prev => ({ ...prev, [index]: false }));
                        if (index === currentIndex) {
                          onMessagingVideoPlayingChange?.(false);
                        }
                      }}
                      playbackStarted={Boolean(hasVideoStarted[index])}
                      pauseWhenOutOfView
                      onScrollPause={() => {
                        setHasVideoStarted((prev) => ({ ...prev, [index]: false }));
                        setPlayingVideos((prev) => ({ ...prev, [index]: false }));
                        if (index === currentIndex) {
                          onMessagingVideoPlayingChange?.(false);
                          onVideoPlaybackStartedChange?.(false);
                        }
                      }}
                    />
                  </div>
                )}

                {isShowDuration &&
                  !chatNativeVideoChrome &&
                  mediaItem.type_file === MEDIA_TYPE.VIDEO &&
                  !playingVideos[index] &&
                  !hasVideoStarted[index] &&
                  !isLock && (
                    <div className="video_time_badge">
                      <i className='fa-solid fa-video'></i>
                      {formatDuration(mediaItem.duration)}
                    </div>
                  )}
              </div>
            ))
          )}
        </div>
        {dotsPlacement === 'overlay' ? dotsNode : null}
      </div>

      {dotsPlacement === 'below' ? dotsNode : null}

    </>
  );

};

export default BundleSlider;
